import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { APP_STORE_URL, appStoreReviews, parentGuides } from "./content";
import usePageMetadata from "./usePageMetadata";

const groups = [
    { title: "By age", links: parentGuides.slice(0, 3) },
    { title: "By skill", links: parentGuides.slice(3, 5) },
    { title: "Features", links: parentGuides.slice(5) },
];

export default function MathGamesHub() {
    const [activeReview, setActiveReview] = useState(0);
    const [reviewsPerView, setReviewsPerView] = useState(1);
    const lastReviewPosition = Math.max(0, appStoreReviews.length - reviewsPerView);

    useEffect(() => {
        const tabletQuery = window.matchMedia("(min-width: 640px)");
        const desktopQuery = window.matchMedia("(min-width: 1280px)");

        const updateReviewsPerView = () => {
            const nextReviewsPerView = desktopQuery.matches ? 3 : tabletQuery.matches ? 2 : 1;
            setReviewsPerView(nextReviewsPerView);
            setActiveReview((current) => Math.min(current, Math.max(0, appStoreReviews.length - nextReviewsPerView)));
        };

        updateReviewsPerView();
        tabletQuery.addEventListener("change", updateReviewsPerView);
        desktopQuery.addEventListener("change", updateReviewsPerView);

        return () => {
            tabletQuery.removeEventListener("change", updateReviewsPerView);
            desktopQuery.removeEventListener("change", updateReviewsPerView);
        };
    }, []);

    usePageMetadata({
        title: "Math Games for Kids",
        description: "Find age-appropriate MathMinds games and parent guides for multiplication, fractions, offline learning, and ad-free math practice.",
    });

    return (
        <div className="mx-auto grid w-full max-w-[1200px] min-w-0 gap-8 overflow-hidden px-4 py-8 min-[380px]:py-10 sm:gap-10 sm:px-8 sm:py-12 lg:grid-cols-[250px_minmax(0,1fr)] lg:overflow-visible lg:py-16">
            <aside className="min-w-0 self-start rounded-3xl border border-[#E9E2CD] bg-white p-5 sm:p-6 lg:sticky lg:top-24" aria-label="Parent guide navigation">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#C37C00] min-[380px]:text-sm min-[380px]:tracking-[0.16em]">For parents</p>
                <div className="mt-5 grid min-w-0 gap-5 min-[430px]:grid-cols-3 lg:mt-0 lg:block">
                {groups.map((group) => (
                    <section key={group.title} className="min-w-0 lg:mt-6">
                        <h2 className="font-bold text-[#283C43]">{group.title}</h2>
                        <ul className="mt-2 space-y-2">
                            {group.links.map((link) => (
                                <li key={link.path} className="min-w-0">
                                    <NavLink to={link.path} className="break-words text-sm text-[#327560] hover:underline">{link.label}</NavLink>
                                </li>
                            ))}
                        </ul>
                    </section>
                ))}
                </div>
            </aside>

            <article className="w-full min-w-0 overflow-hidden">
                <p className="break-words text-xs font-bold uppercase tracking-[0.14em] text-[#C37C00] min-[380px]:text-base min-[380px]:tracking-[0.18em]">MathMinds parent guides</p>
                <h1 className="mt-3 font-chango text-3xl leading-tight text-[#327560] min-[380px]:text-4xl sm:text-5xl">Math games for kids</h1>
                <p className="mt-5 max-w-3xl text-base leading-7 text-[#56635E] sm:mt-6 sm:text-lg sm:leading-8">
                    Choose games by age, learning skill, or the way your family likes to play. These guides help parents match practice to a child’s current confidence and goals.
                </p>

                <div className="mt-8 grid min-w-0 gap-5 sm:mt-10 md:grid-cols-2">
                    {parentGuides.map((guide) => (
                        <NavLink key={guide.path} to={guide.path} className="min-w-0 rounded-3xl border border-[#E9E2CD] bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:border-[#86B7A5] sm:p-6">
                            <h2 className="text-xl font-bold text-[#327560]">{guide.label}</h2>
                            <p className="mt-3 leading-7 text-[#56635E]">{guide.description}</p>
                            <span className="mt-5 inline-block font-bold text-[#327560]">Read guide →</span>
                        </NavLink>
                    ))}
                </div>

                <section className="mt-10 rounded-3xl bg-[#DDF5ED] p-5 sm:mt-12 sm:p-8">
                    <h2 className="font-chango text-2xl text-[#327560]">Ready to play?</h2>
                    <p className="mt-3 leading-7 text-[#56635E]">Explore the complete MathMinds game menu and choose a skill to practice.</p>
                    <div className="mt-6 flex flex-col gap-3 min-[440px]:flex-row min-[440px]:flex-wrap">
                        <NavLink to="/games/memory" className="rounded-full bg-[#F9B818] px-6 py-3 text-center font-bold hover:bg-[#FFCA47]">Open the games</NavLink>
                        <NavLink to="/#daily-challenges" className="rounded-full border-2 border-[#327560] px-6 py-2.5 text-center font-bold text-[#327560] hover:bg-white/50">Learn about daily challenges</NavLink>
                    </div>
                </section>

                <section id="family-profiles" className="mt-10 min-w-0 scroll-mt-24 rounded-3xl bg-[#FFF0C9] p-5 sm:mt-12 sm:p-8">
                    <p className="break-words text-xs font-bold uppercase tracking-[0.14em] text-[#C37C00] min-[380px]:text-base min-[380px]:tracking-[0.18em]">One app for the whole family</p>
                    <h2 className="mt-3 font-chango text-2xl leading-tight text-[#327560] min-[380px]:text-3xl">Math Games for Multiple Children: Up to 3 Profiles</h2>
                    <h3 className="mt-5 text-xl font-bold text-[#283C43]">Separate Profiles for Up to 3 Children</h3>
                    <div className="mt-4 space-y-4 leading-7 text-[#56635E]">
                        <p>Every child learns at their own pace. MathMinds lets families create up to three child profiles in one app, making it easier for siblings to enjoy learning together.</p>
                        <p>Each child has their own points and leaderboard ranking, so children can follow their individual achievements and participate in daily challenges using their own profile.</p>
                        <p>Whether you have one child or three, MathMinds makes it easy for the whole family to enjoy math and brain-training games together.</p>
                        <p className="font-bold text-[#327560]">One family. Up to 3 profiles. Every child’s progress is their own.</p>
                    </div>
                </section>

                <section id="parent-reviews" className="mt-10 min-w-0 scroll-mt-24 border-t border-[#E9E2CD] pt-10 sm:mt-12 sm:pt-12">
                    <p className="break-words text-xs font-bold uppercase tracking-[0.14em] text-[#C37C00] min-[380px]:text-base min-[380px]:tracking-[0.18em]">Selected public App Store reviews</p>
                    <h2 className="mt-3 font-chango text-2xl leading-tight text-[#327560] min-[380px]:text-3xl">What Parents Say About MathMinds</h2>
                    <p className="mt-5 max-w-3xl leading-8 text-[#56635E]">Every child learns differently. MathMinds makes math practice feel more like play, with interactive activities for counting, arithmetic, multiplication, fractions, memory, and concentration.</p>

                    <div className="mt-8 w-full min-w-0" role="region" aria-roledescription="carousel" aria-label="Selected App Store reviews">
                        <div className="w-full min-w-0 overflow-hidden">
                            <div
                                className="flex items-stretch gap-5 transition-transform duration-500 ease-out motion-reduce:transition-none"
                                style={{ transform: `translateX(calc(-${activeReview} * ((100% + 1.25rem) / ${reviewsPerView})))` }}
                            >
                                {appStoreReviews.map((review, index) => (
                                    <figure
                                        key={review.title}
                                        className="flex min-w-0 shrink-0 flex-col rounded-3xl bg-white p-5 shadow-sm min-[380px]:p-6 sm:p-7 xl:p-8"
                                        style={{ flexBasis: `calc((100% - ${(reviewsPerView - 1) * 1.25}rem) / ${reviewsPerView})` }}
                                        aria-roledescription="slide"
                                        aria-label={`${index + 1} of ${appStoreReviews.length}`}
                                        aria-hidden={index < activeReview || index >= activeReview + reviewsPerView}
                                    >
                                        <h3 className="text-xl font-bold text-[#327560]">{review.title}</h3>
                                        <blockquote className="mt-4 flex-1 text-base leading-7 text-[#56635E] sm:text-lg sm:leading-8 xl:text-base xl:leading-7">“{review.quote}”</blockquote>
                                        <figcaption className="mt-6 text-sm font-semibold text-[#6B756F]">App Store review · {review.date}</figcaption>
                                    </figure>
                                ))}
                            </div>
                        </div>

                        <div className="mt-5 grid grid-cols-2 items-center gap-3 sm:flex sm:flex-wrap sm:justify-between sm:gap-4">
                            <button
                                type="button"
                                onClick={() => setActiveReview((current) => Math.max(0, current - 1))}
                                disabled={activeReview === 0}
                                className="order-2 min-w-0 rounded-full border-2 border-[#327560] px-3 py-2 text-sm font-bold text-[#327560] transition hover:bg-[#DDF5ED] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#327560] disabled:cursor-not-allowed disabled:opacity-40 sm:order-1 sm:px-5 sm:text-base"
                                aria-label="Show previous review"
                            >
                                ← Previous
                            </button>

                            <div className="order-1 col-span-2 flex w-full items-center justify-center gap-3 sm:order-2 sm:w-auto" aria-label="Choose a review position">
                                {Array.from({ length: lastReviewPosition + 1 }, (_, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        onClick={() => setActiveReview(index)}
                                        className={`h-3 w-3 rounded-full transition ${index === activeReview ? "bg-[#327560]" : "bg-[#C8D8D2] hover:bg-[#86B7A5]"}`}
                                        aria-label={`Show reviews starting at review ${index + 1}`}
                                        aria-current={index === activeReview ? "true" : undefined}
                                    />
                                ))}
                            </div>

                            <button
                                type="button"
                                onClick={() => setActiveReview((current) => Math.min(lastReviewPosition, current + 1))}
                                disabled={activeReview === lastReviewPosition}
                                className="order-3 min-w-0 rounded-full border-2 border-[#327560] px-3 py-2 text-sm font-bold text-[#327560] transition hover:bg-[#DDF5ED] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#327560] disabled:cursor-not-allowed disabled:opacity-40 sm:px-5 sm:text-base"
                                aria-label="Show next review"
                            >
                                Next →
                            </button>
                        </div>
                        <p className="sr-only" aria-live="polite">Showing reviews {activeReview + 1} through {Math.min(activeReview + reviewsPerView, appStoreReviews.length)} of {appStoreReviews.length}</p>
                    </div>

                    <p className="mt-8 leading-7 text-[#56635E]">We appreciate families who take the time to share their feedback. Their experiences help us continue improving MathMinds and making learning through play more enjoyable.</p>
                    <p className="mt-4 text-sm leading-6 text-[#6B756F]">These are selected excerpts from publicly visible App Store reviews. They are not presented as a representative survey of all MathMinds customers, and MathMinds does not claim that every reviewer is a verified parent.</p>
                    <p className="mt-5 font-bold text-[#327560]">Want to see more feedback? <a href={APP_STORE_URL} target="_blank" rel="noreferrer" className="underline">Read reviews on the MathMinds App Store page ↗</a></p>
                </section>
            </article>
        </div>
    );
}
